import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import Accounts from "./Accounts";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
// Hooks
import { useAccountGroups } from "./Accounts/hooks/useAccountGroups";
import { useAccounts } from "./Accounts/hooks/useAccounts";
import { useCurrencies } from "./Accounts/hooks/useCurrencies";
import { useAccountSettingsUIState, AccountGroup } from "./AccountSettings/hooks/useAccountSettingsUIState";
import { useAccountSettingsHelpers } from "./AccountSettings/hooks/useAccountSettingsHelpers";
import { useAccountSettingsHandlers } from "./AccountSettings/hooks/useAccountSettingsHandlers";

// Components
import SortableGroupCard from "./AccountSettings/components/SortableGroupCard";
import AccountDialog from "./AccountSettings/components/AccountDialog";
import AddGroupDialog from "./AccountSettings/components/AddGroupDialog";
import EditGroupDialog from "./AccountSettings/components/EditGroupDialog";
import ManageDetailsDialog from "./AccountSettings/components/ManageDetailsDialog";

import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  IconButton,
  MenuItem,
  Divider,
  Menu,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import LoadingSpinner from "../components/LoadingSpinner";
import { ACCOUNT_GROUPS_CONFIG } from "../config/ui";

interface AccountSettingsProps {
  workspaceId: string;
}

function AccountSettings({ workspaceId }: AccountSettingsProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Data fetching hooks
  const accountGroups = useAccountGroups(workspaceId);
  const { accounts, loading, setAccounts } = useAccounts(workspaceId);
  const { currencies, defaultCurrency } = useCurrencies(workspaceId);

  // Convert AccountGroupDoc to AccountGroup for compatibility
  const groups: AccountGroup[] = accountGroups.map((g) => ({
    id: g.id,
    name: g.name,
    description: g.description,
    order: g.order,
    createdAt: g.createdAt,
    details: g.details,
  }));

  const setGroups = (newGroups: AccountGroup[] | ((prev: AccountGroup[]) => AccountGroup[])): void => {
    // This is a placeholder - groups are managed by useAccountGroups
    // For drag-and-drop reordering, we'll need to update Firestore directly
    // The handlers hook will handle this
  };

  // UI state hook
  const uiState = useAccountSettingsUIState();

  // Helpers hook
  const helpers = useAccountSettingsHelpers({
    accounts,
    groups,
    currencies,
    defaultCurrency,
    editingGroupName: uiState.editingGroupName,
    accountFormData: uiState.accountFormData,
    editingAccount: uiState.editingAccount,
    initialAccountFormData: uiState.initialAccountFormData,
    showAccountDetails: uiState.showAccountDetails,
    initialShowAccountDetails: uiState.initialShowAccountDetails,
  });

  // Handlers hook
  const handlers = useAccountSettingsHandlers({
    workspaceId,
    groups,
    setGroups: (newGroups: AccountGroup[] | ((prev: AccountGroup[]) => AccountGroup[])) => {
      // Groups are managed by useAccountGroups, but we need to handle updates
      // The handlers will update Firestore directly
    },
    accounts,
    setAccounts,
    defaultCurrency,
    helpers,
    uiState,
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Track when user is dragging a group for auto-scroll near edges (same as currency reorder)
  const [isDraggingGroup, setIsDraggingGroup] = useState(false);
  const [groupsExpanded, setGroupsExpanded] = useState(true);
  const groupListScrollRef = useRef<HTMLDivElement>(null);

  const handleGroupDragEnd = useCallback(
    (event: Parameters<typeof handlers.handleDragEnd>[0]) => {
      setIsDraggingGroup(false);
      handlers.handleDragEnd(event);
    },
    [handlers]
  );

  // Auto-scroll when dragging group near top/bottom so user can reorder to off-screen positions
  useEffect(() => {
    if (!isMobile || !isDraggingGroup) return;
    const container = groupListScrollRef.current;
    if (!container) return;

    const EDGE_ZONE = 0.25;
    const SCROLL_STEP = 12;

    const handlePointerMove = (e: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const relativeY = y / rect.height;
      if (relativeY < EDGE_ZONE) {
        container.scrollTop -= SCROLL_STEP;
      } else if (relativeY > 1 - EDGE_ZONE) {
        container.scrollTop += SCROLL_STEP;
      }
    };

    document.addEventListener("pointermove", handlePointerMove, { passive: true });
    return () => document.removeEventListener("pointermove", handlePointerMove);
  }, [isMobile, isDraggingGroup]);

  if (loading) {
    return <LoadingSpinner fullPage={true} />;
  }

  const groupsNeedScroll = groups.length >= ACCOUNT_GROUPS_CONFIG.SCROLL_THRESHOLD;

  // Max height for groups list when expanded (scroll when more than 4 groups)
  const getGroupsListMaxHeight = (): string => {
    if (groupsNeedScroll) {
      return `${ACCOUNT_GROUPS_CONFIG.LIST_MAX_HEIGHT_WHEN_SCROLL}px`;
    }
    const adaptiveHeight =
      ACCOUNT_GROUPS_CONFIG.BASE_CARD_HEIGHT +
      (groups.length * ACCOUNT_GROUPS_CONFIG.GROUP_ITEM_HEIGHT);
    return `${adaptiveHeight}px`;
  };

  return (
    <Box sx={{ p: 0, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Account Groups Section - Collapsible */}
      <Accordion
        expanded={groupsExpanded}
        onChange={() => setGroupsExpanded((prev) => !prev)}
        disableGutters
        sx={{
          marginBottom: 1.25,
          flexShrink: 0,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          "&:before": { display: "none" },
          boxShadow: "none",
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5" sx={{ fontSize: isMobile ? "1.25rem" : undefined }}>
            Account Groups {groups.length > 0 && `(${groups.length})`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ flexDirection: "column", display: "flex", p: isMobile ? 1.5 : 2, pt: 0 }}>
          <Box
            ref={groupListScrollRef}
            sx={{
              maxHeight: groupsExpanded ? getGroupsListMaxHeight() : "none",
              minHeight: 0,
              overflowY: groupsNeedScroll ? "auto" : "visible",
              overflowX: "hidden",
              pr: groupsNeedScroll ? 1 : 0,
              WebkitOverflowScrolling: "touch",
            }}
          >
            {groups.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No account groups configured
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use the button below to create your first group
                </Typography>
              </Box>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={() => setIsDraggingGroup(true)}
                onDragEnd={handleGroupDragEnd}
                onDragCancel={() => setIsDraggingGroup(false)}
              >
                <SortableContext
                  items={groups.map((g) => g.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack spacing={0.5}>
                    {groups.map((group) => {
                      const inUse = helpers.isGroupInUse(group.name);
                      const accountsUsingGroup = helpers.getAccountsUsingGroup(group.name);

                      return (
                        <SortableGroupCard
                          key={group.id}
                          group={group}
                          inUse={inUse}
                          accountsUsingGroup={accountsUsingGroup}
                          onMenuOpen={handlers.handleGroupMenuOpen}
                        />
                      );
                    })}
                  </Stack>
                </SortableContext>
              </DndContext>
            )}
          </Box>

          <Box sx={{ pt: 1.5, flexShrink: 0, borderTop: 1, borderColor: "divider", mt: 1 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handlers.handleAddClick}
              sx={{
                py: { xs: 1.25, sm: 1.5 },
                fontSize: { xs: "0.9375rem", sm: "1rem" },
              }}
            >
              Create Group
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

        {/* Accounts Section - Use shared Accounts component with ordering enabled */}
        <Paper 
          elevation={isMobile ? 0 : 1} 
          sx={{ 
            flex: 1, 
            minHeight: 0, 
            display: "flex", 
            flexDirection: "column",
            p: 0,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Accounts workspaceId={workspaceId} enableOrdering={true} hideTransferButton={true} />
          </Box>
        </Paper>

      {/* Account Menu */}
      {uiState.accountMenuAnchor && (
        <Menu
          anchorEl={uiState.accountMenuAnchor}
          open={Boolean(uiState.accountMenuAnchor)}
          onClose={handlers.handleAccountMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handlers.handleEditAccount}>Edit</MenuItem>
          <MenuItem onClick={handlers.handleDuplicateAccount}>Duplicate</MenuItem>
          <Divider />
          <MenuItem onClick={handlers.handleDeleteAccountClick} sx={{ color: "error.main" }}>
            Delete
          </MenuItem>
        </Menu>
      )}

      {/* Group Menu */}
      {uiState.groupMenuAnchor && (
        <Menu
          anchorEl={uiState.groupMenuAnchor}
          open={Boolean(uiState.groupMenuAnchor)}
          onClose={handlers.handleGroupMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handlers.handleEditGroupFromMenu}>Edit</MenuItem>
          <MenuItem onClick={handlers.handleManageDetailsFromMenu}>Manage Details</MenuItem>
          <Divider />
          <MenuItem 
            onClick={handlers.handleDeleteGroupFromMenu} 
            disabled={uiState.selectedGroupInUse}
            sx={{ color: "error.main" }}
          >
            Delete
          </MenuItem>
        </Menu>
      )}

      {/* Create/Edit Account Dialog */}
      <AccountDialog
        open={uiState.editAccountDialogOpen}
        onClose={() => {
          // onClose is handled by AccountDialog component
        }}
        uiState={uiState}
        handlers={handlers}
        helpers={helpers}
        groups={groups}
        accounts={accounts}
        currencies={currencies}
        defaultCurrency={defaultCurrency}
      />

      {/* Delete Account Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={uiState.deleteAccountDialogOpen}
        onClose={() => {
          uiState.setDeleteAccountDialogOpen(false);
          uiState.setSelectedAccount(null);
        }}
        onConfirm={handlers.handleDeleteAccountConfirm}
        itemName={uiState.selectedAccount?.accountName || ""}
        itemType="account"
        requireNameConfirmation={true}
      />

      {/* Add Group Dialog */}
      <AddGroupDialog
        open={uiState.addDialogOpen}
        onClose={() => {
          // onClose is handled by AddGroupDialog component
        }}
        uiState={uiState}
        handlers={handlers}
        groups={groups}
      />

      {/* Edit Group Dialog */}
      <EditGroupDialog
        open={uiState.editDialogOpen}
        onClose={() => {
          // onClose is handled by EditGroupDialog component
        }}
        uiState={uiState}
        handlers={handlers}
      />

      {/* Manage Details Dialog */}
      <ManageDetailsDialog
        open={uiState.detailsDialogOpen}
        onClose={() => {
          // onClose is handled by ManageDetailsDialog component
        }}
        uiState={uiState}
        handlers={handlers}
        helpers={helpers}
        accounts={accounts}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={uiState.deleteDialogOpen}
        onClose={() => {
          uiState.setDeleteDialogOpen(false);
          uiState.setGroupToDelete("");
        }}
        onConfirm={handlers.handleDeleteConfirm}
        itemName={uiState.groupToDelete}
        itemType="account group"
      />

      {/* Snackbar for demo mode alerts */}
      <Snackbar
        open={uiState.snackbarOpen}
        autoHideDuration={6000}
        onClose={() => {
          uiState.setSnackbarOpen(false);
          uiState.setSnackbarElement(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => {
          uiState.setSnackbarOpen(false);
          uiState.setSnackbarElement(null);
        }} severity="info" sx={{ width: "100%" }}>
          {uiState.snackbarElement || uiState.snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AccountSettings;
